import React from 'react';
import cn from 'classnames';
import { useSelector } from 'react-redux';

import { isExternalReferrer } from 'helpers/index.js';
import marketplace from 'cfg/marketplace.json';
import MelodyCarousel from 'components/common/MelodyCarousel';
import Grid from 'containers/layout/Grid';
import ProductCard from 'components/common/ProductCard';
import ProductUtils from 'helpers/ProductUtils';
import useMartyContext from 'hooks/useMartyContext';
import { FormattedJanusReco } from 'types/mafia';
import ColorSwatchWrapper from 'components/search/ColorSwatches';
import { getProductWithRelatedStyles } from 'helpers/RecoUtils';
import { track } from 'apis/amethyst';
import { evProductInteract } from 'events/search';
import { Product } from 'constants/searchTypes';
import { ProductWithRelations } from 'types/calypso';
import { RECOMMENDATION_CTA_CLICK } from 'constants/amethystEnums';
import { getBadgeId, getRowHasBadge } from 'helpers/BadgeUtils';
import { formatHeartsForSlots } from 'helpers/HeartUtils';
import { evRecommendationClick } from 'events/recommendations';

import css from 'styles/components/productdetail/recos.scss';

const {
  recos: { showRecosOnTopForExternalReferrer },
  search: { msaImageParams, enableColorSwatches }
} = marketplace;

interface Props {
  additionalMelodyCardProductClasses?: any; // TODO ts type this if/when MCP is typed
  belowImageRenderer?: (...args: any) => JSX.Element | null;
  id?: string;
  isVertical?: boolean;
  heartsData?: any; // TODO ts type this when `hearts` are typed
  noBackground?: boolean;
  onRecoClicked: (...args: any) => void;
  recos: FormattedJanusReco[];
  recoType?: string;
  showTitle?: boolean;
  title?: string;
  /** Allows you to specify how many visible cards are in the carousel at one time */
  numberOfVisibleCards?: number;
  intersectionRef?: (node?: Element | null | undefined) => void;
  /** Display the recos in a gridlayout given the number of columns */
  numberOfGridColumns?: number;
  /** Ref for the title of the reco <h2> */
  titleRef?: React.RefObject<HTMLHeadingElement>;
  isFullMaxWidth?: boolean;
  isHydraMobileTwoRecos?: boolean;
  isInModal?: boolean;
}

const Recos = (props: Props) => {
  const {
    showTitle = true,
    title = 'You Might Also Like',
    id = null,
    isVertical,
    onRecoClicked,
    recos,
    recoType,
    numberOfVisibleCards,
    intersectionRef,
    numberOfGridColumns,
    titleRef,
    isFullMaxWidth = false,
    isHydraMobileTwoRecos = false,
    belowImageRenderer,
    isInModal
  } = props;
  const { testId } = useMartyContext();
  const recoProductRelations = useSelector(state => state.products.recoProductRelations);

  if (!recos || !(recos.length > 0)) {
    return null;
  }

  let showRecosOnTop = false;
  let isTopRecos = false;
  let isBottomRecos = false;
  if (showRecosOnTopForExternalReferrer) {
    showRecosOnTop = isExternalReferrer();
    if (recoType === 'subRecos') {
      isTopRecos = showRecosOnTop;
      isBottomRecos = !showRecosOnTop;
    }
  }

  const amethystRecoType = ProductUtils.translateRecoTitleToAmethystWidget(title);
  const carouselControlInteractionHandler = () =>
    track(() => [
      evRecommendationClick,
      {
        recommendationType: 'PRODUCT_RECOMMENDATION',
        recommendationSource: 'EP13N',
        widgetType: amethystRecoType
      }
    ]);

  const rowHasBadge = getRowHasBadge(recos, recoProductRelations);

  const recoElements = recos.map((reco, index) => {
    const { link, productUrl, styleId } = reco;
    const url = link || productUrl;
    const productWithRelatedStyles = getProductWithRelatedStyles(styleId, recoProductRelations);
    const { relatedStyles, ...productInfo } = productWithRelatedStyles;

    return (
      <Reco
        {...props}
        key={`${reco.productId}-${styleId}`}
        reco={reco}
        index={index}
        productUrl={url}
        onRecoClicked={onRecoClicked}
        amethystRecoType={amethystRecoType}
        belowImageRenderer={belowImageRenderer}
        relatedStyles={enableColorSwatches && relatedStyles ? relatedStyles : []}
        productInfo={productInfo}
        isFullMaxWidth={isFullMaxWidth}
        isMobileTwoRecos={isHydraMobileTwoRecos}
        rowHasBadge={rowHasBadge}
        isInModal={isInModal}
      />
    );
  });

  return (
    <div
      ref={intersectionRef}
      className={cn(css.recoCarousel, { [css.bottomRecos]: isBottomRecos, [css.fullMaxWidth]: isFullMaxWidth })}
      data-test-id={testId(`recoCarousel_${amethystRecoType}`)}
    >
      {showTitle && (
        <h2 className={cn({ [css.noPaddingLeft]: isInModal })} ref={titleRef}>
          {title}
        </h2>
      )}
      <div
        style={
          numberOfVisibleCards ?? numberOfGridColumns
            ? ({
                '--width-split': numberOfVisibleCards ?? 1 // fullwidth when in grid layout
              } as React.CSSProperties)
            : undefined
        }
        id={id || undefined}
        data-test-id={testId(id)}
        className={cn(css.recos, {
          [css.gridRecos]: recoType === 'gridRecos',
          [css.topRecos]: isTopRecos,
          [css.bottomRecos]: isBottomRecos,
          [css.noCarousel]: recos.length < 5,
          [css.vertical]: isVertical,
          [css.fullMaxWidth]: isFullMaxWidth
        })}
      >
        {(recos.length > 4 && recoType !== 'gridRecos' && !numberOfGridColumns) || (recos.length > 3 && isTopRecos && !numberOfGridColumns) ? (
          <MelodyCarousel
            showDots={false}
            isFullMaxWidth={isFullMaxWidth}
            leftArrowClick={carouselControlInteractionHandler}
            rightArrowClick={carouselControlInteractionHandler}
            horizontalSwipe={carouselControlInteractionHandler}
          >
            {recoElements}
          </MelodyCarousel>
        ) : numberOfGridColumns ? (
          <Grid columns={numberOfGridColumns}>{recoElements}</Grid>
        ) : (
          recoElements
        )}
      </div>
    </div>
  );
};

Recos.defaultProps = {
  noBackground: false
};

interface RecoProps extends Props {
  amethystRecoType: string | null;
  index: number;
  onRecoClicked: (...args: any[]) => void;
  reco: FormattedJanusReco;
  productUrl?: string;
  productInfo: any; // TODO cleanup various product type definitions (search product, calypso related product)
  relatedStyles: ProductWithRelations[];
  rowHasBadge?: boolean;
  isMobileTwoRecos?: boolean;
}

export const Reco = (props: RecoProps) => {
  const {
    amethystRecoType,
    heartsData = {},
    index,
    onRecoClicked,
    reco,
    belowImageRenderer,
    productInfo,
    relatedStyles,
    isFullMaxWidth,
    rowHasBadge,
    isMobileTwoRecos,
    isInModal
  } = props;
  const { testId } = useMartyContext();
  const { styleId, productId, link, productUrl } = reco;
  const url = link || productUrl;
  const heartsInfo = formatHeartsForSlots(reco, heartsData);
  const recoSource = useSelector(state => state?.recos?.source || '');

  const { badges } = productInfo;

  const recommendedProduct = {
    productId,
    supplementalData: {
      badgeId: getBadgeId(badges)
    }
  };

  const [baseUrl, ...queryParams] = url.split('?');

  if (relatedStyles?.length) {
    const onProductMediaHovered = (product: Product, mainProductStyleId: string) => {
      track(() => [
        evProductInteract,
        {
          mainStyleId: mainProductStyleId,
          interactedProduct: product,
          interactionType: 'HOVER'
        }
      ]);
    };

    return (
      <ColorSwatchWrapper
        {...productInfo}
        relatedStyles={relatedStyles}
        heartsInfo={heartsInfo}
        key={`${productInfo.styleId}-${productInfo.colorId}`}
        testId={testId('productReco')}
        msaImageParams={msaImageParams}
        onClick={() => onRecoClicked({ index, amethystRecoType, recoSource, recommendedProduct })}
        onProductMediaHovered={onProductMediaHovered}
        // reco exclusives
        className={cn(css.recoCard, {
          [css.displayTwoCardTilesMobile]: isMobileTwoRecos,
          [css.displaySixCardTiles]: isFullMaxWidth,
          [css.recoCardInModal]: isInModal
        })}
        productUrl={baseUrl}
        originalQueryString={queryParams[0]}
        CardDetailsTopSlot2={belowImageRenderer} //ColorSwatches uses CardDetailsTopSlot
        showRecosBadge={true}
        badges={badges}
        imageBadgeClassName={rowHasBadge && css.rowHasBadge}
        relativeBadgeClassName={css.recommendationsMobileBadge}
        absoluteBadgeClassName={css.recommendationsNotMobileBadge}
        amethystRecoType={amethystRecoType}
        isMobileBlockLayout
      />
    );
  }

  return (
    <ProductCard
      key={`d-${styleId}`}
      className={cn(css.recoCard, {
        [css.displayTwoCardTilesMobile]: isMobileTwoRecos,
        [css.displaySixCardTiles]: isFullMaxWidth,
        [css.recoCardInModal]: isInModal
      })}
      hearts={heartsInfo}
      msaImageParams={msaImageParams}
      data-test-id={testId('productReco')}
      {...reco}
      makeStarsBlock={productInfo.reviewCount > 0}
      productRating={productInfo.productRating}
      reviewCount={productInfo.reviewCount}
      productUrl={url}
      onClick={() => onRecoClicked({ index, amethystRecoType, recoSource, recommendedProduct, clickThrough: RECOMMENDATION_CTA_CLICK.PRODUCT_TILE })}
      CardDetailsTopSlot={belowImageRenderer}
      showRecosBadge={true}
      badges={badges}
      imageBadgeClassName={rowHasBadge && css.rowHasBadge}
      relativeBadgeClassName={css.recommendationsMobileBadge}
      absoluteBadgeClassName={css.recommendationsNotMobileBadge}
      amethystRecoType={amethystRecoType}
      isMobileBlockLayout
    />
  );
};

export default Recos;
